import { useState, useEffect } from 'react';
import { X, Share, MoreVertical, ArrowDown, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import useOverlayGate from '../hooks/useOverlayGate';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [wantToShow, setWantToShow] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const { t } = useAuth();
  const gate = useOverlayGate('install-prompt', 40);
  // Vraag beurt zodra we willen tonen; laat vallen bij sluiten.
  useEffect(() => {
    if (wantToShow) gate.request();
    else gate.release();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantToShow]);
  const showPrompt = wantToShow && gate.canShow;
  const setShowPrompt = setWantToShow;

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // 14-dagen cooldown na dismiss (was 24u — te agressief, design agent advies)
    const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < DISMISS_COOLDOWN_MS) return;

    // Engagement-gate: alleen tonen wanneer de gebruiker waarde heeft ervaren.
    // - 2e of latere sessie (via localStorage session-counter), OF
    // - Eerste sessie maar al door 600px gescrolld (~3 cards) op de feed.
    const sessionCount = parseInt(localStorage.getItem('pwa-install-session-count') || '0', 10);
    const hasReturnedSession = sessionCount >= 1;
    // Bump counter eenmaal per browser-session via sessionStorage.
    if (!sessionStorage.getItem('pwa-install-session-bumped')) {
      localStorage.setItem('pwa-install-session-count', String(sessionCount + 1));
      sessionStorage.setItem('pwa-install-session-bumped', '1');
    }

    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(ios);

    // Helper: trigger het prompt zodra de engagement-drempel is gehaald.
    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setShowPrompt(true);
    };

    const scrollHandler = () => {
      if (window.scrollY > 600) trigger();
    };
    if (!hasReturnedSession) {
      window.addEventListener('scroll', scrollHandler, { passive: true });
    }

    if (ios) {
      // Returning user → toon na 8s rustig op de achtergrond.
      // Nieuwe user → wacht op scroll-engagement (>600px).
      if (hasReturnedSession) {
        const timer = setTimeout(trigger, 8000);
        return () => {
          clearTimeout(timer);
          window.removeEventListener('scroll', scrollHandler);
        };
      }
      return () => window.removeEventListener('scroll', scrollHandler);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (hasReturnedSession) {
        // Returning user → toon na 6s.
        setTimeout(trigger, 6000);
      }
      // Anders: wacht op scroll-engagement.
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('scroll', scrollHandler);
    };
  }, []);

  const handleAndroidInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
        // Geef feedback dat de installatie gelukt is. Beforeinstallprompt firet
        // sowieso 'appinstalled' wanneer Chrome 'm op het beginscherm zet.
        toast.success('Hoi Buur staat nu op je startscherm 🎉');
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: show manual instructions
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowInstructions(false);
    gate.release();
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showPrompt && !showInstructions) return null;

  // Detailed instruction overlay (iOS or Android fallback)
  if (showInstructions || (isIOS && showPrompt)) {
    return (
      <div
        className="fixed inset-0 z-[10001] bg-black/60 flex items-end sm:items-center justify-center px-4 pb-4 pt-20 animate-in fade-in duration-200"
        onClick={handleDismiss}
        data-testid="install-instructions"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white max-w-md w-full p-5 shadow-2xl animate-in slide-in-from-bottom-4 duration-300 relative"
          style={{ borderRadius: '0px' }}
        >
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1.5 hover:bg-gray-100"
            aria-label="Sluiten"
            data-testid="install-instructions-dismiss"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          <h3 className="font-heading font-bold text-lg text-gray-900 pr-8">
            Installeer Hoi Buur op je telefoon
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Zo zet je 'm op je beginscherm en open je 'm met één tik, net als een echte app.
          </p>

          {/* Belangrijke verduidelijking — vóór de stappen, niet erna —
              zodat gebruikers begrijpen dat ze het zelf in hun browser doen,
              niet via een knop in deze modal. */}
          <div className="mt-3 p-3 bg-[#FFF4E6] border-l-4 border-[#FF9980] text-xs text-gray-700 leading-relaxed">
            <strong>Let op:</strong> deze stappen voer je zelf uit in je browser. Dit
            venster is alleen een handleiding — er gebeurt niks vanzelf.
          </div>

          {isIOS ? (
            <div className="mt-4 space-y-4">
              <Step
                num="1"
                title={
                  <>Tik onderin Safari op het deel-icoontje
                  <span className="inline-flex items-center justify-center w-7 h-7 mx-1 bg-[#007aff] text-white align-middle">
                    <Share className="w-4 h-4" />
                  </span>
                  (een vierkantje met een pijl omhoog).</>
                }
              />
              <Step
                num="2"
                title={<>Scroll in het menu naar beneden en kies <strong>"Zet op beginscherm"</strong>.</>}
              />
              <Step
                num="3"
                title={<>Tik op <strong>"Voeg toe"</strong>. Hoi Buur staat nu als app-icoontje op je beginscherm.</>}
              />
              <div className="mt-4 flex flex-col items-center text-xs text-gray-500">
                <ArrowDown className="w-5 h-5 text-[#FF9980] animate-bounce" />
                <span>Het deel-icoontje vind je hier helemaal onderin</span>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <Step
                num="1"
                title={
                  <>Tik rechtsboven op het menu-icoontje
                  <span className="inline-flex items-center justify-center w-7 h-7 mx-1 bg-gray-200 align-middle">
                    <MoreVertical className="w-4 h-4 text-gray-700" />
                  </span>
                  (drie puntjes op elkaar).</>
                }
              />
              <Step
                num="2"
                title={<>Kies <strong>"App installeren"</strong> of <strong>"Toevoegen aan startscherm"</strong>.</>}
              />
              <Step
                num="3"
                title={<>Bevestig en Hoi Buur staat als app-icoontje op je startscherm.</>}
              />
              <div className="mt-4 flex flex-col items-center text-xs text-gray-500">
                <ArrowUp className="w-5 h-5 text-[#FF9980] animate-bounce" />
                <span>Het menu-icoontje vind je rechtsboven in je browser</span>
              </div>
            </div>
          )}

          <div className="mt-5 flex gap-2">
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm"
              data-testid="install-instructions-close"
            >
              Nu niet
            </button>
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 bg-[#FF9980] hover:bg-[#ff8a6d] text-black font-bold text-sm"
              data-testid="install-instructions-done"
            >
              Ik heb 'm toegevoegd
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Compact bottom banner (Android with deferred prompt available)
  return (
    <div
      className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-[10000] animate-in slide-in-from-bottom-4 duration-300"
      data-testid="install-prompt"
    >
      <div className="bg-white shadow-xl border border-gray-200 p-4 flex items-start gap-3" style={{ borderRadius: '0px' }}>
        <div className="w-12 h-12 bg-gradient-to-br from-[#6699FF] to-[#FF9980] flex items-center justify-center flex-shrink-0">
          <Share className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-sm text-gray-900">
            {t('install.title')}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            Voeg toe aan je beginscherm voor snelle toegang. Geen download via een appstore nodig.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleAndroidInstall}
              className="px-4 py-1.5 bg-[#FF9980] text-black text-xs font-bold hover:bg-[#ff8a6d] active:scale-95 transition-all"
              style={{ borderRadius: '0px' }}
              data-testid="install-prompt-btn"
            >
              {t('install.button')}
            </button>
            <button
              onClick={() => setShowInstructions(true)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 underline hover:text-gray-900"
              data-testid="install-prompt-help"
            >
              Hoe doe ik dat?
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-gray-100 transition-colors flex-shrink-0"
          aria-label="Sluiten"
          data-testid="install-prompt-dismiss"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

const Step = ({ num, title }) => (
  <div className="flex items-start gap-3">
    <span className="flex-shrink-0 w-7 h-7 bg-[#FF9980] text-black flex items-center justify-center font-heading font-bold text-sm">
      {num}
    </span>
    <p className="text-sm text-gray-800 leading-relaxed flex-1 pt-0.5">{title}</p>
  </div>
);

export default InstallPrompt;
